import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

// API hook for the Google Business Profile tooling: AI suggestions for the
// location↔entertainment matching (the matching itself is saved via
// useGoogleReview().bulkMatchLocations), and the opening-hours comparison/write.
const useGoogleBusiness = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  // POST /api/v1/googleBusiness/match/ai
  const aiSuggestMatches = useCallback(
    async ({ onlyUnmatched = true, entertainmentIds = null } = {}) => {
      try {
        const body = { onlyUnmatched };
        if (Array.isArray(entertainmentIds) && entertainmentIds.length) {
          body.entertainmentIds = entertainmentIds;
        }
        return await sendRequest({
          url: '/api/v1/googleBusiness/match/ai',
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body,
        });
      } catch (err) {
        toast.error('Fehler beim Erzeugen der KI-Vorschläge');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // GET /api/v1/googleBusiness/openingHours/overview
  const getOpeningHoursOverview = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/googleBusiness/openingHours/overview',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden des Öffnungszeiten-Vergleichs');
      throw err;
    }
  }, [authCtx, sendRequest]);

  // PUT /api/v1/googleBusiness/openingHours/:entertainmentId
  // body is either { source: 'internal' } or { week: { '0': [{ start, end }], … } }.
  const updateOpeningHours = useCallback(
    async (entertainmentId, body) => {
      try {
        return await sendRequest({
          url: `/api/v1/googleBusiness/openingHours/${entertainmentId}`,
          method: 'PUT',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body,
        });
      } catch (err) {
        toast.error('Fehler beim Übertragen der Öffnungszeiten an Google');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    aiSuggestMatches,
    getOpeningHoursOverview,
    updateOpeningHours,
  };
};

export default useGoogleBusiness;
